import { Task, WorkItemType } from '@/types';

type SprintChainNode = {
  id?: string;
  sprintId?: string | null;
  sprint?: { id?: string } | null;
  parent?: SprintChainNode | null;
};

export const TIME_LOG_SPRINT_REQUIRED_MESSAGE =
  'Time can only be logged on tasks or bugs assigned to a sprint. Assign the work item (or its parent story) to a sprint first.';

/** Direct sprint on row or inherited via loaded parent chain. */
export function getEffectiveSprintId(task: SprintChainNode): string | null {
  let current: SprintChainNode | null | undefined = task;
  const visited = new Set<string>();

  while (current) {
    const sprintId = current.sprintId || current.sprint?.id;
    if (sprintId) return sprintId;
    const parentId = current.parent?.id;
    if (!parentId || (current.id && visited.has(parentId))) break;
    if (current.id) visited.add(current.id);
    current = current.parent;
  }

  return null;
}

export function taskIsInSprint(task: SprintChainNode): boolean {
  return getEffectiveSprintId(task) !== null;
}

export function canLogTimeOnTask(
  task: Pick<Task, 'assigneeId' | 'status'>,
  userId?: string,
  userRole?: string
): boolean {
  if (!userId || task.status === 'DONE') return false;
  const isPrivileged =
    userRole === 'ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'MANAGER';
  return isPrivileged || task.assigneeId === userId;
}

export function isLoggableWorkItemType(type: WorkItemType | string): boolean {
  return type === WorkItemType.TASK || type === WorkItemType.BUG;
}

/** Assignee/role + TASK/BUG + sprint (direct or parent). */
export function taskIsEligibleForTimeLog(
  task: Pick<Task, 'assigneeId' | 'status' | 'type' | 'sprintId' | 'sprint' | 'parent' | 'id'>,
  userId?: string,
  userRole?: string,
): boolean {
  if (!isLoggableWorkItemType(task.type)) return false;
  if (!canLogTimeOnTask(task, userId, userRole)) return false;
  return taskIsInSprint(task);
}

export function isTimesheetWeekLocked(status?: string | null): boolean {
  return status === 'SUBMITTED' || status === 'APPROVED';
}

export const TIMESHEET_LOCKED_MESSAGE =
  'This week is locked while submitted or approved. Ask your manager to reject if you need to make changes.';
